import EventEmitter from 'events';
import createRateLimitedRunnerDefault from '../../helpers/rateLimitedRunner';

const getQualityLimitationReason = (stats) => {
  const qualityLimitationReasons = stats.video?.layers
    .map(({ qualityLimitationReason }) => qualityLimitationReason).filter(Boolean);
  if (!qualityLimitationReasons?.length) {
    return null;
  }
  if (qualityLimitationReasons.includes('bandwidth')) {
    return 'bandwidth';
  }
  if (qualityLimitationReasons.includes('cpu')) {
    return 'cpu';
  }
  return 'other';
};

const hasCodecChanged = (prevStats, currentStats) =>
  currentStats.video?.layers.some((videoLayer) => {
    if (!videoLayer.codec) {
      return false;
    }
    const prevVideoLayer = prevStats?.video?.layers
      .find(layer => layer.width === videoLayer.width && layer.height === videoLayer.height);
    return prevVideoLayer?.codec && prevVideoLayer.codec !== videoLayer.codec;
  });

const hasResolutionChanged = (prevStats, currentStats) => {
  if (!prevStats) {
    return false;
  }
  if (prevStats.video?.layers.length !== currentStats.video?.layers.length) {
    return true;
  }
  return currentStats.video?.layers.some(videoLayer =>
    !prevStats.video?.layers
      .find(layer => layer.width === videoLayer.width && layer.height === videoLayer.height));
};

const getLogPayloadFromStats = (ev) => {
  const videoLayersStats = ev.stats?.video?.layers;
  if (!videoLayersStats) return null;
  const {
    simulcastLayersVideoWidth,
    simulcastLayersVideoHeight,
    videoScalabilityMode,
    videoQualityLimitationReason,
    videoCodec,
  } = videoLayersStats.reduce((acc, layer) => {
    acc.simulcastLayersVideoWidth.push(layer.width);
    acc.simulcastLayersVideoHeight.push(layer.height);
    acc.videoScalabilityMode.push(layer.scalabilityMode);
    acc.videoQualityLimitationReason.push(layer.qualityLimitationReason);
    acc.videoCodec.push(layer.codec);
    return acc;
  }, {
    simulcastLayersVideoWidth: [],
    simulcastLayersVideoHeight: [],
    videoScalabilityMode: [],
    videoQualityLimitationReason: [],
    videoCodec: [],
  });
  return {
    simulcastLayersVideoWidth: simulcastLayersVideoWidth.join(','),
    simulcastLayersVideoHeight: simulcastLayersVideoHeight.join(','),
    videoScalabilityMode: videoScalabilityMode.join(','),
    videoQualityLimitationReason: videoQualityLimitationReason.join(','),
    videoCodec: videoCodec.join(','),
  };
};

const createPublisherVideoQualityChangedChecker = (deps) => {
  const ee = new EventEmitter();
  const { getPcStats, parseStats, logAnalyticsEvent } = deps;
  const createRateLimitedRunner = deps.createRateLimitedRunner ?? createRateLimitedRunnerDefault;
  const videoQualityChangeLogger = createRateLimitedRunner((videoQualityChangedEvent) => {
    logAnalyticsEvent('videoQualityChanged', 'Event', getLogPayloadFromStats(videoQualityChangedEvent), { reason: videoQualityChangedEvent.reason });
  });
  let lastStats;
  let lastEventReason;

  const processStats = (stats) => {
    const parsedStats = parseStats(stats);
    const prevLastStats = lastStats;
    lastStats = parsedStats;
    let event;

    const qualityLimitationReason = getQualityLimitationReason(parsedStats);
    if (qualityLimitationReason) {
      if (lastEventReason === qualityLimitationReason) {
        return;
      }
      lastEventReason = qualityLimitationReason;
      event = {
        reason: qualityLimitationReason,
        stats: parsedStats,
      };
      ee.emit('videoQualityChanged', event);
      videoQualityChangeLogger.run(event);
      return;
    }

    if (hasCodecChanged(prevLastStats, parsedStats)) {
      lastEventReason = null;
      event = {
        reason: 'codecChange',
        stats: parsedStats,
      };
      ee.emit('videoQualityChanged', event);
      videoQualityChangeLogger.run(event);
      return;
    }

    if (hasResolutionChanged(prevLastStats, parsedStats)) {
      lastEventReason = null;
      event = {
        reason: 'resolutionChange',
        stats: parsedStats,
      };
      ee.emit('videoQualityChanged', event);
      videoQualityChangeLogger.run(event);
      return;
    }

    if (lastEventReason) {
      lastEventReason = null;
      event = {
        reason: 'none',
        stats: parsedStats,
      };
      ee.emit('videoQualityChanged', event);
      videoQualityChangeLogger.run(event);
    }
  };

  const getStats = () => {
    getPcStats((err, stats) => {
      if (!err) {
        processStats(stats);
      }
    });
  };

  return Object.assign(ee, {
    check() {
      getStats();
    },

    destroy() {
      ee.removeAllListeners();
    },
  });
};

export default createPublisherVideoQualityChangedChecker;
